import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { CohortIntegrationFormModal } from '@modules/integrationsV2/components/CohortIntegrationFormModal';
import { CreateOrUpdateCohortGroupModalContent } from '@modules/integrationsV2/components/CreateOrUpdateCohortGroupModalContent';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getIsCohortFileUploadIntegration,
  getIsCohortUserIdentifierIntegration,
  getOAuthRedirectionURL,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { getAddIntegrationFormDrawerContent } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import {
  IntegrationChannelViewFragment,
  IntegrationType,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface UseOnConnectNewIntegrationOptions {
  integrationChannel: IntegrationChannelViewFragment;
  oAuthURL?: string;
}

export const useOnConnectNewIntegration = ({
  integrationChannel,
  oAuthURL,
}: UseOnConnectNewIntegrationOptions) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const { openModal } = useMuiModalContext();

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const onConnect = useCallback(
    (integrationType: IntegrationType) => {
      if (
        getIsCohortFileUploadIntegration({
          channelType: integrationChannel.type,
          integrationType,
        })
      ) {
        openModal({
          contentComponent: () => <CreateOrUpdateCohortGroupModalContent />,
          modalProps: {
            fullWidth: true,
            maxWidth: 'md',
          },
        });
        return;
      }

      if (
        getIsCohortUserIdentifierIntegration({
          channelType: integrationChannel.type,
          integrationType,
        })
      ) {
        openModal({
          contentComponent: () => (
            <CohortIntegrationFormModal channelType={integrationChannel.type} />
          ),
          modalProps: {
            fullWidth: true,
            maxWidth: 'lg',
            PaperProps: {
              elevation: 3,
            },
          },
        });
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
        return;
      }

      const drawerContentComponent = getAddIntegrationFormDrawerContent({
        integrationChannel,
        integrationType,
        onDrawerClose: () => {}, // Not required, because we are not gonna use it
      });

      if (drawerContentComponent) {
        dispatch({
          type: IntegrationsActionType.SET_VIEW,
          payload: {
            selectedIntegrationFormToConnect: {
              integrationChannel,
              integrationType,
            },
            isAddDrawerOpen: false,
          },
        });
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
        return;
      }

      // Lastly, we rely on redirecting to OAuth URL
      if (oAuthURL) {
        const redirectURL = getOAuthRedirectionURL({
          channelType: integrationChannel.type,
          oAuthURL,
          tenantSlug,
          integrationType,
        });

        window.open(redirectURL, '_self');
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
      }
    },
    [dispatch, integrationChannel, oAuthURL, openModal, tenantSlug],
  );

  return onConnect;
};
